// All geometry is normalized to the unmirrored source, not the preview element.
export const GUIDED_FRAMING_TARGET = Object.freeze({
  centerX: 0.50,
  centerY: 0.45,
  minCenterX: 0.40,
  maxCenterX: 0.60,
  minCenterY: 0.35,
  maxCenterY: 0.55,
  minHeight: 0.22,
  maxHeight: 0.50,
  height: 0.36,
  faceAspectRatio: 0.75,
});

export const getGuidedFramingTargetBounds = (sourceAspectRatio = 1) => {
  const aspect = Number.isFinite(sourceAspectRatio) && sourceAspectRatio > 0 ? sourceAspectRatio : 1;
  const { centerX, centerY, height, faceAspectRatio } = GUIDED_FRAMING_TARGET;
  const width = Math.min(1, height * faceAspectRatio / aspect);
  return { x: centerX - width / 2, y: centerY - height / 2, width, height };
};

export const getGuidedFramingCorrection = (face) => {
  if (!face?.bounds) return null;
  const { bounds } = face;
  const target = GUIDED_FRAMING_TARGET;
  if (bounds.height > target.maxHeight) return 'faceMoveFarther';
  if (bounds.height < target.minHeight) return 'faceMoveCloser';
  const x = bounds.x + bounds.width / 2;
  const y = bounds.y + bounds.height / 2;
  if (x < target.minCenterX) return 'faceMoveRight';
  if (x > target.maxCenterX) return 'faceMoveLeft';
  if (y < target.minCenterY) return 'faceMoveDown';
  if (y > target.maxCenterY) return 'faceMoveUp';
  return null;
};

// Match object-fit: contain, including letterboxing in constrained preview layouts.
export const getGuidedPreviewRect = (sourceAspectRatio, width, height) => {
  if (!(sourceAspectRatio > 0) || !(width > 0) || !(height > 0)) return null;
  const sourceWidth = Math.min(width, height * sourceAspectRatio);
  const sourceHeight = sourceWidth / sourceAspectRatio;
  return {
    x: (width - sourceWidth) / 2,
    y: (height - sourceHeight) / 2,
    width: sourceWidth,
    height: sourceHeight,
  };
};

export const createGuidedVisualGuidance = () => {
  let missingSince = null;
  return {
    update(face, now = performance.now()) {
      if (!face) {
        missingSince ??= now;
        return now - missingSince >= 2000 ? 'faceMissing' : null;
      }
      missingSince = null;
      const { landmarks = [], luma } = face;
      if (luma?.mean < 0.18) return 'faceTooDark';
      if (luma?.mean > 0.90 || luma?.brightFraction > 0.1) return 'faceTooBright';
      const framingCorrection = getGuidedFramingCorrection(face);
      if (framingCorrection) return framingCorrection;
      const left = landmarks[33];
      const right = landmarks[263];
      const nose = landmarks[1];
      const mouth = landmarks[13];
      if (!left || !right || !nose || !mouth) return null;
      const aspect = face.aspectRatio || 1;
      const eyeDistance = Math.hypot((right.x - left.x) * aspect, right.y - left.y);
      const roll = Math.atan2(Math.abs(right.y - left.y), Math.abs(right.x - left.x) * aspect) * 180 / Math.PI;
      if (roll > 10) return 'faceLevel';
      const eyeX = (left.x + right.x) / 2;
      if (Math.abs(nose.x - eyeX) * aspect / Math.max(eyeDistance, 1e-6) > 0.15) return 'faceCamera';
      const eyeY = (left.y + right.y) / 2;
      const vertical = (nose.y - eyeY) / Math.max(mouth.y - eyeY, 1e-6);
      if (vertical < 0.25 || vertical > 0.75) return 'cameraEyeLevel';
      return null;
    },
  };
};
